import { useEffect, useMemo, useState } from "react";
import { Loader2, X, Search, Check } from "lucide-react";
import { toast } from "react-toastify";
import {
  listFubContacts,
  importFubSelected,
} from "../../utils/firebasefunctions";

// Pick-and-choose Follow Up Boss import: loads ALL FUB contacts, lets the user
// search and check the ones they want, then imports only those (de-duped on the
// backend). Replaces the old one-click "import everyone with the Sync Tag".
export default function FubImportModal({ userId, onClose, onImported }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await listFubContacts();
        if (active) setContacts(res.contacts || []);
      } catch (e) {
        if (active) {
          setError(
            /not connected/i.test(e?.message || "")
              ? "Connect Follow Up Boss in Settings first."
              : e?.message || "Could not load contacts.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.email, c.phone].some((v) =>
        String(v || "").toLowerCase().includes(q),
      ),
    );
  }, [contacts, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((c) => next.delete(c.id));
      else filtered.forEach((c) => next.add(c.id));
      return next;
    });

  const doImport = async () => {
    const people = contacts.filter((c) => selected.has(c.id));
    if (people.length === 0) return;
    setImporting(true);
    try {
      const res = await importFubSelected(userId, people);
      const n = res?.imported ?? people.length;
      toast.success(
        `Imported ${n} contact${n === 1 ? "" : "s"} from Follow Up Boss`,
      );
      if (onImported) await onImported();
      onClose();
    } catch (e) {
      toast.error(e?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[80vh] flex flex-col pointer-events-auto overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-[#1E293B] text-white">
            <h3 className="text-base font-semibold">
              Import from Follow Up Boss
            </h3>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-6 h-6 animate-spin" />
              Loading your Follow Up Boss contacts…
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center px-6 text-center text-sm text-slate-600">
              {error}
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 space-y-2">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, email, or phone…"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-[#1E293B]"
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                    />
                    Select all{search ? " matching" : ""} ({filtered.length})
                  </label>
                  <span className="font-medium text-slate-700">
                    {selected.size} selected
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="text-center text-slate-400 text-sm py-10">
                    No contacts match “{search}”.
                  </div>
                ) : (
                  filtered.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {c.name || "(no name)"}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={doImport}
                  disabled={selected.size === 0 || importing}
                  className="px-5 py-2 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {importing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Check size={14} />
                  )}
                  Import Selected ({selected.size})
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
